// /perks: the Pixally Founding Partners.
//
// One source for the partner cards (pages/PerksPage.jsx) and the prerendered
// HTML crawlers read (scripts/prerender.mjs). Plain JS, no JSX, so the build
// scripts can import it.
//
// Rules (docs/KNOWN-ISSUES.md section 2.0): a card may say what members get
// (`offer`), never the redemption code; the codes live inside Pixally. Every
// partner fact comes from the partner's own site.
//
// Partner links are ordinary followed links, rel="noopener" only. Ryan's call
// on 5 Oct 2026, replacing rel="sponsored" (29 Sep): he was told Google treats a
// link given in exchange for something of value, here a member discount, as one
// that should be marked sponsored, and chose followed links anyway. Do not put
// "sponsored" or "nofollow" back without asking him.

export const ROUTE = '/perks';

// Each partner link carries UTM tags, so the partner's analytics shows the visit
// as Pixally's (utm_source=pixally, utm_campaign=perks), separately from any
// other link to them. `content` tells the places apart: 'perks_page' here, and
// e.g. 'app' for the codes inside Pixally later. A partner who gives us their own
// tracking link (one that already has utm_source) keeps theirs untouched.
export const partnerHref = (url, content = 'perks_page') => {
  const u = new URL(url);
  if (u.searchParams.has('utm_source')) return u.toString();
  u.searchParams.set('utm_source', 'pixally');
  u.searchParams.set('utm_medium', 'referral');
  u.searchParams.set('utm_campaign', 'perks');
  u.searchParams.set('utm_content', content);
  return u.toString();
};
export const UPDATED_AT = '2026-10-05';
export const SEO_TITLE = 'Pixally Perks: Founding Partner Discounts for Wedding Photographers and Videographers';
export const SEO_DESCRIPTION =
  'Exclusive discounts for Pixally members from hand-picked Founding Partners: video editing, AI video editing, video file repair, websites and filmmaking education.';

// Live Founding Partners rendered as full featured cards. Each `id` doubles as
// the scroll anchor for its "Jump to" pill and must match the CATEGORIES entry.
export const PARTNERS = [
  {
    // A founder favorite, added as a thank-you. Ryan's short personal note is woven
    // into the description (not a separate block) so this card stays as tight as the
    // others; `offerSuffix` makes the deal read "an exclusive discount on all repairs".
    id: 'cat-video-file-repair',
    brand: 'treasured',
    name: 'Treasured',
    logo: { src: '/partners/treasured/logo.png', alt: 'Treasured by Aero Quartet' },
    category: 'Video File Repair',
    tagline: 'The footage you thought was lost.',
    description:
      "The best in the business at video file repair, recovering corrupt footage no software can open. A note from Ryan, Pixally's founder: they've saved my ass countless times when nothing else could, and I love these folks for it.",
    url: 'https://aeroquartet.com/treasured/',
    visitLabel: 'Visit Treasured',
    offerSuffix: 'all repairs',
    thumbAspect: 'aspect-video', // app screenshots are ~16:9
    portfolio: [
      { src: '/partners/treasured/work-1.jpg', alt: 'Treasured video repair app showing a 95 percent repair score' },
      { src: '/partners/treasured/work-2.jpg', alt: 'Treasured deep media scan of a corrupted video file' },
      { src: '/partners/treasured/work-3.jpg', alt: 'Treasured repairing a damaged video file' },
    ],
  },
  {
    id: 'cat-video-editing',
    brand: 'uncut-gems',
    name: 'Uncut Gems',
    logo: { src: '/partners/uncut-gems/gem-mark.png', alt: 'Uncut Gems' },
    category: 'Video Editing',
    tagline: 'Every gem starts uncut.',
    description:
      'Outsourced wedding video and photo editing for creatives. Professional editors who match your style, hit your deadlines, and make your work look like you did it. Trusted by 300+ studios.',
    url: 'https://uncutgems.com/',
    visitLabel: 'Visit Uncut Gems',
    thumbAspect: 'aspect-video', // wedding film stills are 16:9
    portfolio: [
      { src: '/partners/uncut-gems/work-1.jpg', alt: 'Uncut Gems wedding film still, Jackie and Joshua' },
      { src: '/partners/uncut-gems/work-2.jpg', alt: 'Uncut Gems wedding film still, Chelsea and Nicholas' },
      { src: '/partners/uncut-gems/work-3.jpg', alt: 'Uncut Gems wedding film still, Alyssa and Brandon' },
    ],
  },
  {
    id: 'cat-websites',
    brand: 'creative-cabana',
    name: 'Creative Cabana',
    logo: { src: '/partners/creative-cabana/logo.png', alt: 'Creative Cabana' },
    category: 'Websites',
    tagline: 'Some brands pass through. Others leave a mark.',
    description:
      'A full-service bespoke branding and website studio for wedding photographers, videographers, and creative professionals. Strategy, brand, copy, and web design created together, built to make your work unmistakable.',
    url: 'https://creativecabana.co/',
    visitLabel: 'Visit Creative Cabana',
    thumbAspect: 'aspect-[3/2]', // laptop mockups are 3:2 landscape
    portfolio: [
      { src: '/partners/creative-cabana/web-1.jpg', alt: 'Creative Cabana website design shown on a laptop' },
      { src: '/partners/creative-cabana/web-2.jpg', alt: 'Creative Cabana website design shown on a MacBook' },
      { src: '/partners/creative-cabana/web-3.jpg', alt: 'Creative Cabana website design shown on a laptop' },
    ],
  },
  {
    // `offer` replaces the [X]% placeholder with the partner's real member offer.
    // The redemption code itself still never appears on this page (see the offer
    // bar below): codes live inside Pixally, as a reason to sign up.
    id: 'cat-ai-video-editing',
    brand: 'plotline',
    name: 'Plotline',
    logo: { src: '/partners/plotline/logo.png', alt: 'Plotline' },
    category: 'AI Video Editing',
    tagline: 'Start every edit with structure, not a blank timeline.',
    description:
      'Story-first editing for Mac and the web, built by filmmakers. Send a timeline from DaVinci Resolve, Final Cut Pro or Premiere Pro, and Plotline transcribes it, finds the story and sends a structured first cut back to your editor, built from your own footage. Vows, toasts and speeches become a structured assembly.',
    url: 'https://www.plotline.pro/',
    visitLabel: 'Visit Plotline',
    offer: '20% off the first six months on monthly plans',
    thumbAspect: 'aspect-[3/2]',
    portfolio: [
      { src: '/partners/plotline/work-1.jpg', alt: 'Plotline setting up a project from an interview timeline sent from the editor' },
      { src: '/partners/plotline/work-2.jpg', alt: 'Plotline transcript with named speakers and a waveform' },
      { src: '/partners/plotline/work-3.jpg', alt: 'Plotline story engine choosing a runtime and the themes of a film' },
    ],
  },
  {
    // Facts from Matt's own pages (whoismatt.com, courses.whoismatt.com), Oct 2026.
    // No member offer yet, so the card keeps the [X]% placeholder.
    id: 'cat-education',
    brand: 'whoismatt',
    name: 'WhoisMatt',
    logo: { src: '/partners/whoismatt/logo.png', alt: 'WhoisMatt' },
    category: 'Education',
    tagline: 'Create better videos with smarter filmmaking and editing.',
    description:
      'Courses, guides and coaching from wedding filmmaker and YouTuber Matt Johnson, who has taught more than 6,000 filmmakers since 2015: editing in Premiere Pro and DaVinci Resolve, story-driven wedding films, pricing and growing a wedding film business, plus his own LUTs.',
    url: 'https://whoismatt.com/',
    visitLabel: 'Visit WhoisMatt',
    thumbAspect: 'aspect-video', // course cards are 16:9
    portfolio: [
      { src: '/partners/whoismatt/work-1.jpg', alt: 'Wedding Film Framework course for Premiere Pro by Matt Johnson' },
      { src: '/partners/whoismatt/work-2.jpg', alt: 'Ultimate Wedding Filmmaker course by Matt Johnson' },
      { src: '/partners/whoismatt/work-3.jpg', alt: 'WhoisMatt LUTs, available now' },
    ],
  },
];

// Category names only — no invented partner names. `available` marks a live
// partner (rendered as a featured card above); the rest are coming soon.
export const CATEGORIES = [
  { name: 'Websites', id: 'cat-websites', available: true },
  { name: 'Video Editing', id: 'cat-video-editing', available: true },
  { name: 'AI Video Editing', id: 'cat-ai-video-editing', available: true },
  { name: 'Video File Repair', id: 'cat-video-file-repair', available: true },
  { name: 'Music Licensing', id: 'cat-music-licensing' },
  { name: 'Sound Design', id: 'cat-sound-design' },
  { name: 'Album Design', id: 'cat-album-design' },
  { name: 'AI Culling & Editing', id: 'cat-ai-culling-editing' },
  { name: 'Contracts & Legal', id: 'cat-contracts-legal' },
  { name: 'Insurance', id: 'cat-insurance' },
  { name: 'Education', id: 'cat-education', available: true },
];
